import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format, addMonths, subMonths, isWithinInterval, startOfDay, differenceInCalendarDays } from 'date-fns';

const BRAND_BLUE = '#003663';
const BRAND_YELLOW = '#fbd215';
const BRAND_LIGHT_BLUE = 'rgba(0, 54, 99, 0.1)';

const DatePickerWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const MainInput = styled.div`
    height: ${props => props.customHeight || '56px'};
  padding: 0 16px;
  border-radius: 12px;
    border: 2px solid ${props => props.isOpen ? '#003663' : '#e7e7e7'};
  background: ${props => props.isInHeader ? 'white' : 'white'};
  font-size: 16px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: ${props => props.isOpen ? 1001 : 1};
  width: ${props => {
    // If it's in header mode, adjust width based on variant
    if (props.isInHeader) {
      // Banner needs narrower input in the header
      if (props.variant === 'banner') {
        return props.isMobile ? '100%' : '455px'; // Reduced from 480px to 400px
      }
      // Default/hotel needs wider input to match the original
      return props.isMobile ? '100%' : '700px';
    }
    return '100%';
  }};

  @media (max-width: 768px) {
        width: 100%;
        max-width: 100vw;
        height: ${props => props.customHeight || '58px'};
    border: none;
    background: white;
    margin-left: 0;
    padding: 0 8px;
    box-sizing: border-box;
  }

    &:hover {
    border-color: ${props => props.isInHeader ? '#003663' : '#003663'};
    }
`;

const MiniInputsContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  padding-left: ${props => props.isInHeader ? '48px' : '48px'};
  position: relative;
  margin: 0;
  width: 100%;

  @media (max-width: 768px) {
    padding-left: 40px;
    padding-right: 8px;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  /* Remove the vertical line between dates */
  /* &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 1px;
    height: 24px;
    background: ${BRAND_BLUE};
    opacity: 0.2;
    display: ${props => props.isRange ? 'block' : 'none'};
  } */
`;

const MiniInput = styled.div`
  flex: 1;
  padding: 8px 16px;
  border-radius: 8px;
  background: ${props => props.active ? `${BRAND_LIGHT_BLUE}` : 'transparent'};
  /* Always use brand blue for text, irrespective of state */
  color: ${BRAND_BLUE};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: ${props => props.value ? '600' : '400'};
  position: relative;
  text-align: center;
  border: none;

  @media (max-width: 768px) {
    padding: 6px 8px;
    font-size: 13px;
    min-width: 0;
    flex-shrink: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: ${BRAND_LIGHT_BLUE};
  }
`;

const CalendarIcon = styled(CalendarTodayIcon)`
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    color: ${BRAND_BLUE};
    pointer-events: none;
  font-size: 20px;
`;

const CalendarContainer = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: ${props => {
    if (props.variant === 'banner') {
      return 'translateX(calc(-50% + 200px))';
    }
    return 'translateX(calc(-50% + 100px))';
  }};
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 24px;
  min-width: 700px;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: env(safe-area-inset-bottom, 0);
    padding-bottom: calc(env(safe-area-inset-bottom, 0) + 8px);
    transform: none;
    min-width: unset;
    width: 100%;
    height: 100vh;
    border-radius: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden; /* Prevent body scroll */
  }
`;

const CalendarHeader = styled.div`
  background: ${BRAND_BLUE};
  margin: -24px -24px 24px;
  padding: 20px 24px;
  border-radius: 12px 12px 0 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 88px;
  width: calc(100% + 48px);
  margin-left: -24px;

  @media (max-width: 768px) {
    margin: 0;
    width: 100%;
    border-radius: 0;
    padding: 12px 8px;
    flex-direction: row;
    gap: 8px;
    min-height: unset;
    justify-content: flex-start;
    align-items: center;
    box-sizing: border-box;
  }
`;

const BackButton = styled.button`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
      color: white;
    padding: 8px;
    margin-right: 0;
    cursor: pointer;
    min-width: 40px; /* Ensure minimum width for the button */
    
    svg {
      font-size: 24px;
    }
  }
`;

const ResetButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px 16px;
  font-size: 14px;
  opacity: 0.8;
  transition: all 0.2s ease;
  margin-right: 12px;

    &:hover {
    opacity: 1;
  }

  @media (max-width: 768px) {
    display: none; /* Hide reset button in header on mobile */
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${BRAND_BLUE};
  transition: all 0.2s ease;
  z-index: 1002;

  @media (max-width: 768px) {
    display: none; /* Hide navigation arrows on mobile */
  }

  /* Disable hover effect on desktop arrows */
  &:hover {
    background: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.prev {
    left: -20px;
  }

  &.next {
    right: -20px;
  }
`;

const MonthsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    overflow-y: auto;
    padding: 16px;
    padding-bottom: 100px; /* Add space for fixed mobile actions */
    flex: 1;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    
    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }
    
    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const MonthHeader = styled.div`
  text-align: left;
  margin-bottom: 16px;
  
  .month-title {
    font-size: 18px;
    font-weight: 600;
    color: ${BRAND_BLUE};
  }

  @media (max-width: 768px) {
    text-align: center;
    margin-bottom: 24px;
    padding: 0 16px;
    
    .month-title {
      font-size: 20px;
    }
  }
`;

const RangeBackground = styled.div`
  position: absolute;
  background: rgba(0, 54, 99, 0.1);
  border-radius: 20px;
  z-index: 1;
  pointer-events: none;
  transition: all 0.2s ease;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(40px, 1fr));
  gap: 2px;
  position: relative;
  
  .weekday {
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #666;
    font-weight: 500;
    position: relative;
    z-index: 2;
  }

  .day {
    height: 40px;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: ${BRAND_BLUE};
    cursor: pointer;
    position: relative;
    z-index: 3;
    margin: 0 auto;
    background: transparent !important;
    border-radius: 50%;
    transition: all 0.2s ease;

    &:hover:not(.range-start):not(.range-end) {
      background: rgba(0, 54, 99, 0.1) !important;
    }

    &.range-start, &.range-end {
      background: ${BRAND_BLUE} !important;
      color: white;
      font-weight: 600;
      
      &:hover {
        background: ${BRAND_BLUE} !important;
        transform: scale(1.05);
      }
    }

    &.disabled {
      color: #ccc;
      cursor: not-allowed;
      
      &:hover {
        background: transparent !important;
        transform: none;
      }
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    gap: 0;
    grid-template-columns: repeat(7, 1fr);
    
    .weekday {
      width: 100%;
      height: 48px;
      font-size: 16px;
    }
    
    .day {
      width: 100%;
      height: 48px;
      font-size: 16px;
    }
  }
`;

const MobileActions = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 16px 20px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    background: white;
    border-top: 1px solid rgba(0, 54, 99, 0.1);
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1001;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    min-height: 80px;
    
    /* Ensure visibility across all mobile browsers */
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    will-change: transform;
    
    /* Fallback for browsers that don't support env() */
    @supports not (padding-bottom: env(safe-area-inset-bottom)) {
      padding-bottom: 20px;
    }
    
    /* Additional compatibility fixes */
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    
    /* Ensure proper stacking context */
    isolation: isolate;
    
    /* Force GPU acceleration on problematic browsers */
    -webkit-perspective: 1000;
    perspective: 1000;
    
    /* Ensure flexbox works on older browsers */
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-justify-content: space-between;
    -ms-flex-pack: justify;
    justify-content: space-between;
    -webkit-align-items: center;
    -ms-flex-align: center;
    align-items: center;
    
    /* Prevent content from being cut off */
    overflow: visible;
    
    /* Ensure white background is solid */
    background-color: #ffffff;
    background-image: none;
  }
`;

const MobileButton = styled.button`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 14px 28px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.2s;
    min-height: 48px;
    box-sizing: border-box;
    outline: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    
    /* Ensure buttons are always visible */
    position: relative;
    z-index: 1002;
    
    /* Enhanced cross-browser compatibility */
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    
    /* Ensure flexbox works on older browsers */
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-align-items: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-justify-content: center;
    -ms-flex-pack: center;
    justify-content: center;
    
    /* Prevent text selection and improve touch */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    
    /* Ensure proper text rendering */
    text-rendering: optimizeLegibility;
    
    &.reset {
      background: white;
      background-color: #ffffff;
      border: 2px solid ${BRAND_BLUE};
      color: ${BRAND_BLUE};
      flex: 1;
      -webkit-flex: 1;
      -ms-flex: 1;
      margin-right: 12px;
      
      /* Enhanced hover/active states */
      &:hover, &:active, &:focus {
        background: rgba(0, 54, 99, 0.05);
        background-color: rgba(0, 54, 99, 0.05);
        border-color: ${BRAND_BLUE};
        outline: none;
      }
      
      /* Ensure text color stays consistent */
      &:visited {
        color: ${BRAND_BLUE};
      }
    }
    
    &.done {
      background: ${BRAND_BLUE};
      background-color: ${BRAND_BLUE};
      color: white;
      border: 2px solid ${BRAND_BLUE};
      flex: 2;
      -webkit-flex: 2;
      -ms-flex: 2;
      font-weight: 700;
      letter-spacing: 0.5px;
      
      /* Enhanced hover/active states */
      &:hover, &:active, &:focus {
        background: rgba(0, 54, 99, 0.9);
        background-color: rgba(0, 54, 99, 0.9);
        transform: scale(0.98);
        -webkit-transform: scale(0.98);
        -ms-transform: scale(0.98);
        outline: none;
      }
      
      /* Ensure text color stays white */
      &:visited {
        color: white;
      }
    }
    
    /* Ensure buttons work on touch devices */
    @media (hover: none) and (pointer: coarse) {
      &:hover {
        transform: none;
        -webkit-transform: none;
        -ms-transform: none;
      }
      
      &:active {
        transform: scale(0.95);
        -webkit-transform: scale(0.95);
        -ms-transform: scale(0.95);
      }
    }
  }
`;

const NightsIndicator = styled.div`
  position: absolute;
  bottom: -48px; /* 10px further down from previous -12px */
  left: auto;
  right: 46px; /* 10px more left from previous right:16px */
  background: ${BRAND_BLUE};
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
  z-index: 1003;
  pointer-events: none;

  @media (min-width: 769px) {
    display: none;
  }
`;

export const NewDatePicker = ({
  isRange = false,
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = 'Select date',
  departurePlaceholder = 'Departure date',
  returnPlaceholder = 'Return date',
  className,
  isOpen: controlledIsOpen,
  onOpenChange,
  readOnly = false,
  preventDirectOpen = false,
  customHeight,
  activeInput: externalActiveInput,
  setActiveInput: externalSetActiveInput,
  variant = 'default',
  containerClassName,
  hasError = false,
  onDropdownToggle = null,
  onDoneClick = null
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [internalActiveInput, setInternalActiveInput] = useState('start');
  const wrapperRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [hoveredDate, setHoveredDate] = useState(null);

  // Nights between selected dates
  const nights = value?.startDate && value?.endDate ?
    differenceInCalendarDays(new Date(value.endDate), new Date(value.startDate)) : 0;

  // Use external active input state if provided, otherwise use internal
  const activeInput = externalActiveInput !== undefined ? externalActiveInput : internalActiveInput;
  const setActiveInput = externalSetActiveInput || setInternalActiveInput;

  // Ensure we have valid date objects
  const startDate = value?.startDate ? new Date(value.startDate) : null;
  const endDate = value?.endDate ? new Date(value.endDate) : null;

  // Validate dates
  useEffect(() => {
    if (startDate && isNaN(startDate.getTime())) {
      console.error('Invalid start date:', startDate);
      onChange({ startDate: null, endDate: value?.endDate });
    }
    if (endDate && isNaN(endDate.getTime())) {
      console.error('Invalid end date:', endDate);
      onChange({ startDate: value?.startDate, endDate: null });
    }
  }, [startDate, endDate]);

  // Use controlled open state if provided
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  // Call onDropdownToggle when calendar open state changes
  useEffect(() => {
    if (onDropdownToggle) {;
      onDropdownToggle(isOpen);
    }
  }, [isOpen, onDropdownToggle]);

  const handleOpenChange = (open) => {
    if (preventDirectOpen && open) {
      // Don't open directly in mobile mode
      return;
    }

    if (open && !hasBeenOpened) {
      setHasBeenOpened(true);
    }
    if (onOpenChange) {
      onOpenChange(open);
    } else {
      setInternalIsOpen(open);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        handleOpenChange(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && !value?.startDate && hasBeenOpened) {
      // Remove auto-selection of today's date when calendar opens
      // onChange({ 
      //   startDate: new Date(), 
      //   endDate: value?.endDate 
      // });
      // setActiveInput('end');
    }
  }, [isOpen, hasBeenOpened]);

  // Add check for mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const formatDisplayDate = (value) => {
    if (!value) return placeholder;
    
    try {
      if (isRange) {
        if (value.startDate && value.endDate) {
          const startDate = new Date(value.startDate);
          const endDate = new Date(value.endDate);
          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            return placeholder;
          }
          return `${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`;
        } else if (value.startDate) {
          const startDate = new Date(value.startDate);
          if (isNaN(startDate.getTime())) {
            return placeholder;
          }
          // Use returnPlaceholder which should already be translated
          return `${format(startDate, 'dd/MM/yyyy')} - ${returnPlaceholder || 'Return date'}`;
        }
        return placeholder;
      }
      
      // Single date mode
      const date = value instanceof Date ? value : new Date(value);
      if (isNaN(date.getTime())) {
        return placeholder;
      }
      return format(date, 'dd/MM/yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return placeholder;
    }
  };

  const handleDateClick = (date) => {
    if (!isRange) {
      // For one-way flights, select the start date
      onChange({ startDate: date, endDate: null });
      
      // ✅ MOBILE CHANGE: Only auto-close on desktop, not mobile
      if (!isMobile) {
      handleOpenChange(false);
      }
      return;
    }

    if (activeInput === 'start') {
      // When selecting the first date in a range, clear end date if needed
      onChange({ 
        startDate: date, 
        endDate: null
      });
      // Switch to end date selection mode
      setActiveInput('end');
      // Do NOT close the calendar - we need to select the end date next
    } else {
      // When selecting the end date
      const startDate = value?.startDate;
      if (startDate && date < startDate) {
        // If end date is before start date, swap them
        onChange({ startDate: date, endDate: startDate });
      } else {
        // Normal case: we have a valid date range
        onChange({ startDate: startDate, endDate: date });
      }
      
      // Reset active input to start for next time
      setActiveInput('start');
      
      // ✅ MOBILE CHANGE: Only close after both dates are selected AND we're in desktop mode
      // On mobile, user must click DONE button to close
      if (!isMobile) {
        // Add a small delay to allow UI to update
        setTimeout(() => handleOpenChange(false), 100);
      }
    }
  };

  const isDateInRange = (date) => {
    if (!value?.startDate) return false;
    
    const start = new Date(value.startDate);
    let end;
    
    if (activeInput === 'end' && hoveredDate) {
      // If we're selecting end date and hovering, use hovered date as end
      end = hoveredDate > start ? hoveredDate : start;
    } else if (value?.endDate) {
      // Otherwise use the actual end date if it exists
      end = new Date(value.endDate);
    } else {
      return false;
    }
    
    const current = new Date(date);
    return current > start && current < end;
  };

  const isDateInHoverRange = (date) => {
    if (!value?.startDate || !hoveredDate || activeInput !== 'end') return false;
    
    const start = new Date(value.startDate);
    const current = new Date(date);
    
    if (hoveredDate > start) {
      return current > start && current <= hoveredDate;
    } else {
      return current < start && current >= hoveredDate;
    }
  };

  const isDateSelected = (date) => {
    if (!value?.startDate) return false;
    
    const dateStr = format(date, 'yyyy-MM-dd');
    const startStr = format(new Date(value.startDate), 'yyyy-MM-dd');
    const endStr = value?.endDate ? format(new Date(value.endDate), 'yyyy-MM-dd') : null;
    
    return dateStr === startStr || dateStr === endStr;
  };

  const calculateRangeBackground = (monthDate, isHover = false) => {
    const effectiveStartDate = startDate;
    const effectiveEndDate = isHover && hoveredDate && activeInput === 'end' ? hoveredDate : endDate;
    
    if (!effectiveStartDate || !effectiveEndDate) return null;

    const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    
    // Check if range intersects with this month
    if (effectiveEndDate < monthStart || effectiveStartDate > monthEnd) return null;

    const rangeStart = effectiveStartDate > monthStart ? effectiveStartDate : monthStart;
    const rangeEnd = effectiveEndDate < monthEnd ? effectiveEndDate : monthEnd;

    // Calculate grid positions - more precise calculation
    const getGridPosition = (date) => {
      const day = date.getDate();
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay() || 7;
      const dayIndex = day + firstDay - 2; // -1 for 0-based, -1 for Monday start
      const row = Math.floor(dayIndex / 7) + 1; // +1 for weekday header row
      const col = (dayIndex % 7);
      return { row, col };
    };

    const startPos = getGridPosition(rangeStart);
    const endPos = getGridPosition(rangeEnd);

    const backgrounds = [];
    
    // Grid cell dimensions - responsive
    const cellWidth = 100 / 7; // percentage
    const cellHeight = isMobile ? 48 : 42; // mobile: 48px, desktop: 40px + 2px gap
    const headerHeight = isMobile ? 48 : 40; // weekday header height
    const dayHeight = isMobile ? 48 : 40;
    
    // If same row, create single background
    if (startPos.row === endPos.row) {
      const left = startPos.col * cellWidth + '%';
      const width = ((endPos.col - startPos.col + 1) * cellWidth) + '%';
      const top = (headerHeight + (startPos.row - 1) * cellHeight + 1) + 'px'; // +1 for gap adjustment
      
      backgrounds.push({
        left,
        width,
        top,
        height: dayHeight + 'px'
      });
    } else {
      // Multi-row range
      for (let row = startPos.row; row <= endPos.row; row++) {
        let left, width;
        
        if (row === startPos.row) {
          // First row: from start column to end
          left = startPos.col * cellWidth + '%';
          width = ((7 - startPos.col) * cellWidth) + '%';
        } else if (row === endPos.row) {
          // Last row: from start to end column
          left = '0%';
          width = ((endPos.col + 1) * cellWidth) + '%';
        } else {
          // Middle rows: full width
          left = '0%';
          width = '100%';
        }
        
        const top = (headerHeight + (row - 1) * cellHeight + 1) + 'px';
        
        backgrounds.push({
          left,
          width,
          top,
          height: dayHeight + 'px'
        });
      }
    }

    return backgrounds;
  };

  const renderCalendarMonth = (monthDate) => {
    if (!monthDate || isNaN(monthDate.getTime())) {
      console.error('Invalid monthDate:', monthDate);
      return [];
    }

    try {
      const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const days = [];
      const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

      weekdays.forEach(day => {
        days.push(<div key={`weekday-${day}`} className="weekday">{day}</div>);
      });

      let firstDay = start.getDay() || 7;
      for (let i = 1; i < firstDay; i++) {
        days.push(<div key={`empty-${i}`} className="day"></div>);
      }

      const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
        
        if (isNaN(date.getTime())) {
          console.error('Invalid date created:', { year: monthDate.getFullYear(), month: monthDate.getMonth(), day });
          continue;
        }

        const isDisabled = (minDate && date < startOfDay(minDate)) || 
                          (maxDate && date > startOfDay(maxDate));

        try {
          const dateStr = format(date, 'yyyy-MM-dd');
          const startStr = startDate ? format(startDate, 'yyyy-MM-dd') : null;
          const endStr = endDate ? format(endDate, 'yyyy-MM-dd') : null;
          
          const isStartDate = dateStr === startStr;
          const isEndDate = dateStr === endStr;

          let className = 'day';
          if (isDisabled) className += ' disabled';
          if (isStartDate) className += ' range-start';
          if (isEndDate) className += ' range-end';

          days.push(
            <div
              key={`day-${day}`}
              className={className}
              onClick={() => !isDisabled && handleDateClick(date)}
              onMouseEnter={() => !isDisabled && setHoveredDate(date)}
              onMouseLeave={() => setHoveredDate(null)}
            >
              {day}
            </div>
          );
        } catch (error) {
          console.error('Error formatting date:', error);
          continue;
        }
      }

      return days;
    } catch (error) {
      console.error('Error rendering calendar month:', error);
      return [];
    }
  };

  const renderInput = (isInHeader = false) => (
    <div 
      className={`date-picker-input ${className || ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        background: "#f8f9fa",
        borderRadius: '8px',
        height: '56px',
        cursor: readOnly ? "default" : "pointer",
        border: hasError ? '1px solid #dc3545' : 'none',
        // Make input wider when in opened calendar header
        width: isInHeader ? (isMobile ? 'calc(100vw - 80px)' : '470px') : 'auto',
        maxWidth: isInHeader ? (isMobile ? 'calc(100vw - 80px)' : '470px') : 'none',
        flex: isInHeader && isMobile ? '1' : 'unset',
      }}
    >
      <div style={{
        paddingLeft: '15px',
        display: 'flex',
        alignItems: 'center'
      }}>
        <CalendarTodayIcon style={{ color: hasError ? '#dc3545' : '#003663', fontSize: 20 }} />
      </div>
      <input
        type="text"
        readOnly
        placeholder={placeholder}
        value={formatDisplayDate(value)}
        onClick={() => !readOnly && !preventDirectOpen && handleOpenChange(true)}
        style={{
          padding: "12px 16px",
          paddingLeft: "8px",
          border: 'none',
          background: "transparent",
          cursor: readOnly ? "default" : "pointer",
          height: '56px',
          fontSize: '18px',
          fontWeight: '500',
          width: '100%',
          color: hasError ? '#dc3545' : BRAND_BLUE,
          '::placeholder': {
            color: hasError ? '#dc3545' : '#6c757d',
            opacity: 1
          }
        }}
      />
    </div>
  );

  const renderMobileActions = () => (
    <MobileActions>
      <MobileButton 
        className="reset"
        onClick={() => {
          onChange({ startDate: null, endDate: null });
          setActiveInput('start');
        }}
        style={{
          // Inline fallback styles for maximum compatibility
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '48px',
          flex: 1,
          marginRight: '12px',
          border: `2px solid ${BRAND_BLUE}`,
          backgroundColor: 'white',
          color: BRAND_BLUE,
          borderRadius: '8px',
          fontWeight: '600',
          fontSize: '16px',
          cursor: 'pointer',
          outline: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          appearance: 'none'
        }}
      >
        Clear
      </MobileButton>
      <MobileButton 
        className="done"
        onClick={() => {
          // ✅ ENHANCED: Call onDoneClick callback if provided (for step advancement)
          if (onDoneClick) {
            onDoneClick(value);
          }
          handleOpenChange(false);
          setActiveInput('start');
        }}
        style={{
          // Inline fallback styles for maximum compatibility
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '48px',
          flex: 2,
          border: `2px solid ${BRAND_BLUE}`,
          backgroundColor: BRAND_BLUE,
          color: 'white',
          borderRadius: '8px',
          fontWeight: '700',
          fontSize: '16px',
          cursor: 'pointer',
          outline: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          appearance: 'none',
          letterSpacing: '0.5px'
        }}
      >
        {/* ✅ ENHANCED: Better button text based on selection state */}
        {isRange 
          ? (value?.startDate && value?.endDate 
              ? 'Confirm Dates' 
              : value?.startDate 
                ? 'Return Date' 
                : 'Select Dates')
          : (value?.startDate 
              ? 'Confirm Date' 
              : 'Select Date')
        }
      </MobileButton>
    </MobileActions>
  );

  const renderMonths = () => {
    if (isMobile) {
      // Generate 12 months for mobile scrolling
      const months = [];
      for (let i = 0; i < 12; i++) {
        const monthDate = addMonths(currentDate, i);
        months.push(
          <div key={i}>
            <MonthHeader>
              <span className="month-title">
                {format(monthDate, 'MMMM yyyy')}
              </span>
            </MonthHeader>
            <CalendarGrid>
              {/* Range backgrounds */}
              {calculateRangeBackground(monthDate)?.map((bg, index) => (
                <RangeBackground
                  key={`range-${index}`}
                  style={{
                    left: bg.left,
                    width: bg.width,
                    top: bg.top,
                    height: bg.height
                  }}
                />
              ))}
              {/* Hover range backgrounds */}
              {calculateRangeBackground(monthDate, true)?.map((bg, index) => (
                <RangeBackground
                  key={`hover-range-${index}`}
                  style={{
                    left: bg.left,
                    width: bg.width,
                    top: bg.top,
                    height: bg.height,
                    background: 'rgba(0, 54, 99, 0.08)',
                    opacity: hoveredDate && activeInput === 'end' ? 1 : 0
                  }}
                />
              ))}
              {renderCalendarMonth(monthDate)}
            </CalendarGrid>
          </div>
        );
      }
      return months;
    }

    // Desktop view with 2 months
    return (
      <>
        <div>
          <MonthHeader>
            <span className="month-title">
              {format(currentDate, 'MMMM yyyy')}
            </span>
          </MonthHeader>
          <CalendarGrid>
            {/* Range backgrounds */}
            {calculateRangeBackground(currentDate)?.map((bg, index) => (
              <RangeBackground
                key={`range-${index}`}
                style={{
                  left: bg.left,
                  width: bg.width,
                  top: bg.top,
                  height: bg.height
                }}
              />
            ))}
            {/* Hover range backgrounds */}
            {calculateRangeBackground(currentDate, true)?.map((bg, index) => (
              <RangeBackground
                key={`hover-range-${index}`}
                style={{
                  left: bg.left,
                  width: bg.width,
                  top: bg.top,
                  height: bg.height,
                  background: 'rgba(0, 54, 99, 0.08)',
                  opacity: hoveredDate && activeInput === 'end' ? 1 : 0
                }}
              />
            ))}
            {renderCalendarMonth(currentDate)}
          </CalendarGrid>
        </div>
        <div>
          <MonthHeader>
            <span className="month-title">
              {format(addMonths(currentDate, 1), 'MMMM yyyy')}
            </span>
          </MonthHeader>
          <CalendarGrid>
            {/* Range backgrounds */}
            {calculateRangeBackground(addMonths(currentDate, 1))?.map((bg, index) => (
              <RangeBackground
                key={`range-${index}`}
                style={{
                  left: bg.left,
                  width: bg.width,
                  top: bg.top,
                  height: bg.height
                }}
              />
            ))}
            {/* Hover range backgrounds */}
            {calculateRangeBackground(addMonths(currentDate, 1), true)?.map((bg, index) => (
              <RangeBackground
                key={`hover-range-${index}`}
                style={{
                  left: bg.left,
                  width: bg.width,
                  top: bg.top,
                  height: bg.height,
                  background: 'rgba(0, 54, 99, 0.08)',
                  opacity: hoveredDate && activeInput === 'end' ? 1 : 0
                }}
              />
            ))}
            {renderCalendarMonth(addMonths(currentDate, 1))}
          </CalendarGrid>
        </div>
      </>
    );
  };

  return (
    <DatePickerWrapper ref={wrapperRef} className={containerClassName}>
      {!isOpen && renderInput(false)}

        {isOpen && (
        <>
          <div
            className="react-datepicker-backdrop" 
            onClick={() => handleOpenChange(false)} 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 999
            }}
          />
            <CalendarContainer variant={variant}>
            <CalendarHeader>
              {isMobile && (
                <BackButton onClick={() => handleOpenChange(false)}>
                  <ChevronLeftIcon />
                </BackButton>
              )}
              {renderInput(true)}
              {!isMobile && (
                <ResetButton 
                  onClick={() => {
                    onChange({ startDate: null, endDate: null });
                    setActiveInput('start');
                  }}
                  className="desktop-only"
                >
                  Reset
                </ResetButton>
              )}
              {isMobile && nights > 0 && (
                <NightsIndicator aria-label={`${nights} night${nights>1?'s':''} selected`}>
                  {nights} night{nights>1?'s':''}
                </NightsIndicator>
              )}
            </CalendarHeader>

            {!isMobile && (
              <>
                <NavigationButton
                  className="prev"
                  onClick={() => setCurrentDate(subMonths(currentDate, 1))}
                  disabled={minDate && subMonths(currentDate, 1) < startOfDay(minDate)}
                >
                  <ChevronLeftIcon />
                </NavigationButton>

                <NavigationButton
                  className="next"
                  onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                  disabled={maxDate && addMonths(currentDate, 2) > startOfDay(maxDate)}
                >
                  <ChevronRightIcon />
                </NavigationButton>
              </>
            )}

            <MonthsContainer>
              {renderMonths()}
            </MonthsContainer>
            {renderMobileActions()}
            </CalendarContainer>
        </>
        )}
      </DatePickerWrapper>
  );
}; 